import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut,
  RefreshCw,
  CheckCircle2,
  Clock,
  XCircle,
  Trash2,
  CalendarDays,
  TrendingUp,
  ImageIcon,
  X as XIcon,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const LOGO = "https://customer-assets.emergentagent.com/job_austin-yard-pro/artifacts/h82kv0hn_IMG_4290.png";

const STATUS_COLORS = {
  pending: "text-yellow-400 border-yellow-400/40",
  confirmed: "text-primary border-primary/40",
  completed: "text-muted-foreground border-border",
  cancelled: "text-red-400 border-red-400/40",
};

export default function AdminDashboard() {
  const nav = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, confirmed: 0, completed: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [viewingPhotos, setViewingPhotos] = useState(null); // booking being viewed

  useEffect(() => {
    if (!localStorage.getItem("ironclad_admin_token")) {
      nav("/admin/login");
      return;
    }
    load();
    // eslint-disable-next-line
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [b, s] = await Promise.all([
        api.get("/admin/bookings"),
        api.get("/admin/stats"),
      ]);
      setBookings(b.data);
      setStats(s.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("ironclad_admin_token");
        nav("/admin/login");
        return;
      }
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/bookings/${id}`, { status });
      toast.success(`Marked ${status}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this booking permanently?")) return;
    try {
      await api.delete(`/admin/bookings/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const logout = () => {
    localStorage.removeItem("ironclad_admin_token");
    localStorage.removeItem("ironclad_admin_email");
    nav("/admin/login");
  };

  const filtered = filter ? bookings.filter((b) => b.status === filter) : bookings;

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="admin-dashboard">
      {/* Top bar */}
      <header className="border-b border-border bg-black/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="container-ic flex items-center justify-between py-4">
          <Link to="/" className="flex items-center gap-3">
          <img src={LOGO} alt="Ironclad Yard Services" className="h-11 w-auto object-contain" />
          <span className="text-[10px] tracking-[0.3em] text-primary uppercase border-l border-border pl-3">
            Admin
          </span>
        </Link>
          <div className="flex items-center gap-4">
            <button
              onClick={load}
              className="text-muted-foreground hover:text-primary"
              data-testid="admin-refresh"
              aria-label="Refresh"
            >
              <RefreshCw className={`h-5 w-5 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button
              onClick={logout}
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary"
              data-testid="admin-logout"
            >
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="container-ic py-10">
        <div className="mb-8">
          <div className="overline mb-2">Dashboard</div>
          <h1 className="font-display text-4xl md:text-5xl font-black uppercase tracking-tighter">
            Bookings
          </h1>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border border border-border mb-10">
          {[
            { label: "Total", val: stats.total, icon: TrendingUp },
            { label: "Pending", val: stats.pending, icon: Clock },
            { label: "Confirmed", val: stats.confirmed, icon: CheckCircle2 },
            { label: "Completed", val: stats.completed, icon: CalendarDays },
          ].map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.label} className="bg-background p-6" data-testid={`stat-${s.label.toLowerCase()}`}>
                <Icon className="h-5 w-5 text-primary mb-3" />
                <div className="font-display text-3xl md:text-4xl font-black">{s.val}</div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground mt-1">
                  {s.label}
                </div>
              </div>
            );
          })}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-6">
          {["", "pending", "confirmed", "completed", "cancelled"].map((f) => (
            <button
              key={f || "all"}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 text-xs uppercase tracking-widest border transition-colors ${
                filter === f
                  ? "bg-primary text-primary-foreground border-primary"
                  : "border-border text-muted-foreground hover:border-primary"
              }`}
              data-testid={`filter-${f || "all"}`}
            >
              {f || "All"}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="border border-border overflow-x-auto">
          <table className="w-full text-sm" data-testid="bookings-table">
            <thead className="bg-accent/40 border-b border-border text-xs uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="text-left p-4">Customer</th>
                <th className="text-left p-4 hidden md:table-cell">Service</th>
                <th className="text-left p-4 hidden lg:table-cell">Address</th>
                <th className="text-left p-4">Date</th>
                <th className="text-right p-4">Price</th>
                <th className="text-left p-4">Status</th>
                <th className="text-right p-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="p-10 text-center text-muted-foreground">
                    Loading…
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="p-10 text-center text-muted-foreground">
                    No bookings found.
                  </td>
                </tr>
              ) : (
                filtered.map((b) => (
                  <tr
                    key={b.id}
                    className="border-b border-border last:border-b-0 hover:bg-accent/30"
                    data-testid={`booking-row-${b.id}`}
                  >
                    <td className="p-4">
                      <div className="font-semibold">{b.name}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">{b.phone}</div>
                      {b.email && <div className="text-xs text-muted-foreground">{b.email}</div>}
                      {b.is_veteran && (
                        <span className="inline-block mt-1 text-[10px] uppercase tracking-widest text-primary border border-primary/40 px-2 py-0.5">
                          Vet
                        </span>
                      )}
                    </td>
                    <td className="p-4 hidden md:table-cell">
                      <div>{b.service}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {b.lot_size} · {b.frequency.replace("_", "-")}
                      </div>
                      {b.photos && b.photos.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setViewingPhotos(b)}
                          className="mt-1 inline-flex items-center gap-1 text-[11px] text-primary hover:underline"
                          data-testid={`photos-${b.id}`}
                        >
                          <ImageIcon className="h-3 w-3" /> {b.photos.length} photo
                          {b.photos.length > 1 ? "s" : ""}
                        </button>
                      )}
                    </td>
                    <td className="p-4 hidden lg:table-cell text-muted-foreground">
                      {b.address}
                      {b.city && <div className="text-xs">{b.city}</div>}
                    </td>
                    <td className="p-4 whitespace-nowrap">{b.preferred_date}</td>
                    <td className="p-4 text-right font-display text-lg font-bold">
                      ${b.price_estimate}
                    </td>
                    <td className="p-4">
                      <span
                        className={`inline-block text-[10px] uppercase tracking-widest border px-2 py-1 ${
                          STATUS_COLORS[b.status] || ""
                        }`}
                      >
                        {b.status}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex justify-end gap-1">
                        {b.status !== "confirmed" && (
                          <button
                            onClick={() => updateStatus(b.id, "confirmed")}
                            title="Confirm"
                            className="p-2 text-primary hover:bg-primary/10"
                            data-testid={`confirm-${b.id}`}
                          >
                            <CheckCircle2 className="h-4 w-4" />
                          </button>
                        )}
                        {b.status !== "completed" && (
                          <button
                            onClick={() => updateStatus(b.id, "completed")}
                            title="Complete"
                            className="p-2 text-muted-foreground hover:text-foreground hover:bg-accent"
                            data-testid={`complete-${b.id}`}
                          >
                            <CalendarDays className="h-4 w-4" />
                          </button>
                        )}
                        {b.status !== "cancelled" && (
                          <button
                            onClick={() => updateStatus(b.id, "cancelled")}
                            title="Cancel"
                            className="p-2 text-red-400 hover:bg-red-400/10"
                            data-testid={`cancel-${b.id}`}
                          >
                            <XCircle className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={() => remove(b.id)}
                          title="Delete"
                          className="p-2 text-muted-foreground hover:text-red-400 hover:bg-red-400/10"
                          data-testid={`delete-${b.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </main>

      {viewingPhotos && (
        <div
          className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-6"
          onClick={() => setViewingPhotos(null)}
          data-testid="photo-modal"
        >
          <div
            className="relative max-w-5xl w-full max-h-[90vh] overflow-auto bg-card border border-border p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="overline">Photos</div>
                <div className="font-display text-xl font-bold">{viewingPhotos.name}</div>
                <div className="text-xs text-muted-foreground">
                  {viewingPhotos.service} · {viewingPhotos.address}
                </div>
              </div>
              <button
                onClick={() => setViewingPhotos(null)}
                className="p-2 text-muted-foreground hover:text-foreground"
                aria-label="Close"
                data-testid="photo-modal-close"
              >
                <XIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {viewingPhotos.photos.map((src, i) => (
                <a
                  key={i}
                  href={src}
                  target="_blank"
                  rel="noreferrer"
                  className="block border border-border overflow-hidden"
                  data-testid={`photo-${i}`}
                >
                  <img
                    src={src}
                    alt={`booking-photo-${i}`}
                    className="w-full h-60 object-cover hover:scale-105 transition-transform"
                  />
                </a>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
